/**
 * ReportsPage — Phase 10A.
 *
 * Tabbed container for all three reports:
 *  - Office Manager tab  (admin, manager — Phase 10A)
 *  - Student tab         (future Phase 10B)
 *  - Executive tab       (future Phase 10C — owner, admin only)
 *
 * data-testid: candeza-reports-*
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { PageContainer } from '@/components/layout';
import { useAuth } from '@/context';
import { OfficeManagerReport } from './office-manager/OfficeManagerReport';
import type { Role } from '@/types';

// ── Styled components ─────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: none;
  border-bottom: 3px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ $active, theme }) =>
    $active ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.normal};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  transition: all 0.15s;
  margin-bottom: -2px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const ComingSoon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing.md};
  .icon { font-size: 48px; }
  .label {
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  }
  .sub { font-size: ${({ theme }) => theme.typography.fontSizes.sm}; }
`;

// ── Tab registry ─────────────────────────────────────────────────────────────

type TabId = 'office-manager' | 'student' | 'executive';

interface TabDef {
  id: TabId;
  label: string;
  roles: Role[];
  available: boolean;
}

const TABS: TabDef[] = [
  { id: 'office-manager', label: 'Office Manager', roles: ['owner', 'admin', 'manager'], available: true },
  { id: 'student', label: 'Student Reports', roles: [], available: false },
  { id: 'executive', label: 'Executive', roles: ['owner', 'admin'], available: false },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const Reports: React.FC = () => {
  const { user } = useAuth();
  const userRole = user?.role as Role | undefined;

  const visibleTabs = TABS.filter(
    (t) => t.roles.length === 0 || !userRole || t.roles.includes(userRole),
  );

  const [activeTab, setActiveTab] = useState<TabId>(visibleTabs[0]?.id ?? 'office-manager');
  const currentTab = TABS.find((t) => t.id === activeTab);

  return (
    <PageContainer
      title="Reports"
      subtitle="Operational and analytics reports"
      data-testid="candeza-reports-page"
    >
      <TabBar data-testid="candeza-reports-tabs">
        {visibleTabs.map((tab) => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`candeza-reports-tab-${tab.id}`}
          >
            {tab.label}{!tab.available && ' ✦'}
          </Tab>
        ))}
      </TabBar>

      <div data-testid={`candeza-reports-content-${activeTab}`}>
        {!currentTab?.available ? (
          <ComingSoon data-testid="candeza-reports-coming-soon">
            <div className="icon">📊</div>
            <div className="label">{currentTab?.label}</div>
            <div className="sub">Coming in the next phase.</div>
          </ComingSoon>
        ) : activeTab === 'office-manager' ? (
          <OfficeManagerReport />
        ) : null}
      </div>
    </PageContainer>
  );
};
